import {
  REFERENCE_PERIOD_SELECT_OPTIONS,
  sortReferencePeriods,
} from '~/functions/referencePeriods'

export function parseAnnualReferenceYear(referencePeriod: string): number | null {
  const token = (referencePeriod || '').trim().split(' ')[0]
  const year = Number.parseInt(token, 10)
  return Number.isFinite(year) ? year : null
}

export function reportingDefaultPeriods(referencePeriod: string): string[] {
  const normalized = (referencePeriod || '').trim()
  const year = parseAnnualReferenceYear(normalized)
  if (year === null) {
    return []
  }

  if (!normalized.includes(' ')) {
    return [String(year - 1), String(year)]
  }

  const suffix = normalized.split(' ', 2)[1]
  if (/^[QM]\d/.test(suffix)) {
    return [String(year - 1), String(year)]
  }

  return sortReferencePeriods([String(year - 1), String(year), normalized])
}

export function buildReportingRequestPeriodItems(options: {
  annualOnly: boolean
  quarterly: boolean
  portfolioPeriods?: string[]
}): string[] {
  const years = Array.from({ length: 11 }, (_, index) => String(2020 + index))
  const items: string[] = []

  if (options.annualOnly) {
    items.push(...years)
  } else if (options.quarterly) {
    years.forEach((year) => {
      items.push(`${year} Q1`, `${year} Q2`, `${year} Q3`, `${year} Q4`, year)
    })
  } else {
    items.push(...REFERENCE_PERIOD_SELECT_OPTIONS)
    for (const period of options.portfolioPeriods || []) {
      if (!items.includes(period)) {
        items.push(period)
      }
    }
  }

  return sortReferencePeriods(items)
}

export function reportingStatusChipClasses(status: string): string {
  switch (status) {
    case 'COMPLETE':
      return 'reporting-status-chip reporting-status-chip--complete'
    case 'IN_PROGRESS':
      return 'reporting-status-chip reporting-status-chip--in-progress'
    default:
      return 'reporting-status-chip reporting-status-chip--not-started'
  }
}

export function reportingStatusLabelKey(status: string): string {
  switch (status) {
    case 'COMPLETE':
      return 'status.complete'
    case 'IN_PROGRESS':
      return 'status.inProgress'
    default:
      return 'status.notStarted'
  }
}

export function reportingStatusCountLabelKey(status: string): string {
  switch (status) {
    case 'COMPLETE':
      return 'status.completeCount'
    case 'IN_PROGRESS':
      return 'status.inProgressCount'
    default:
      return 'status.notStartedCount'
  }
}

/** ESG TODOs are binary: COMPLETE, or IN_PROGRESS for anything else. */
export function reportingEsgChipStatus(status: string): string {
  return status === 'COMPLETE' ? 'COMPLETE' : 'IN_PROGRESS'
}

export function reportingTypeLabelKey(requestType: string): string {
  switch (requestType) {
    case 'IMPACT_CARRY':
      return 'type.impactCarry'
    case 'ESG':
      return 'type.esg'
    default:
      return 'type.kpi'
  }
}

export function countReportingVentureStatuses(
  ventures?: Array<{ status?: string | null }> | null,
) {
  const counts = {
    COMPLETE: 0,
    IN_PROGRESS: 0,
    NOT_STARTED: 0,
  }
  for (const venture of ventures ?? []) {
    const status = venture.status || 'NOT_STARTED'
    if (status === 'COMPLETE') {
      counts.COMPLETE += 1
    } else if (status === 'IN_PROGRESS') {
      counts.IN_PROGRESS += 1
    } else {
      counts.NOT_STARTED += 1
    }
  }
  return counts
}

export function isReportingRequestComplete(
  ventures?: Array<{ status?: string | null }> | null,
) {
  const list = ventures ?? []
  return list.length > 0 && list.every((venture) => venture.status === 'COMPLETE')
}

export function aggregateReportingRequestStatus(
  ventures?: Array<{ status?: string | null }> | null,
): 'COMPLETE' | 'IN_PROGRESS' | 'NOT_STARTED' {
  if (isReportingRequestComplete(ventures)) {
    return 'COMPLETE'
  }
  const counts = countReportingVentureStatuses(ventures)
  if (counts.COMPLETE > 0 || counts.IN_PROGRESS > 0) {
    return 'IN_PROGRESS'
  }
  return 'NOT_STARTED'
}
